import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Users, MapPin, Wifi, Coffee, Wind, CheckCircle2, ImageIcon } from "lucide-react";
import { api, formatBRL, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import SpaceGallery from "../../components/SpaceGallery";
import { BookingCalendar } from "../../components/booking/BookingCalendar";
import { SlotPicker } from "../../components/booking/SlotPicker";
import { useSEO } from "../../lib/seo";
import { track } from "../../lib/tracking";
import { saveQuote } from "../../lib/booking-storage";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolvePhoto = (p) => {
  if (!p) return null;
  if (p.startsWith("http") || p.startsWith("data:")) return p;
  if (p.startsWith("/")) return `${BACKEND}${p}`;
  return p;
};

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function EspacoDetalhe() {
  const { id } = useParams();
  const nav = useNavigate();

  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(todayISO());
  const [availability, setAvailability] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // SEO + OG
  useSEO({
    title: space?.name || "Reserve um ambiente",
    description: space?.description ||
      "Reserve seu espaço de coworking em minutos, sem fidelidade. Escritórios, salas de reunião, consultórios e coworking em Piabetá.",
    image: space?.photos?.length ? resolvePhoto(space.photos[0]) : undefined,
    url: typeof window !== "undefined" ? window.location.href : undefined,
    type: "product",
  });

  // Load space
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/public/spaces/${id}`)
      .then(({ data }) => {
        if (!mounted) return;
        setSpace(data);
        track("view_space", { space_id: data.id, space_name: data.name, category: data.category });
      })
      .catch((e) => {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Espaço não encontrado");
        nav("/espacos", { replace: true });
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [id, nav]);

  // Load availability when date changes
  useEffect(() => {
    if (!space) return;
    let mounted = true;
    setSlotsLoading(true);
    setStart(null); setEnd(null); setQuote(null);
    api.get(`/public/spaces/${id}/availability`, { params: { date } })
      .then(({ data }) => mounted && setAvailability(data))
      .catch(() => mounted && setAvailability(null))
      .finally(() => mounted && setSlotsLoading(false));
    return () => { mounted = false; };
  }, [id, date, space]);

  // Auto-quote whenever a full range is selected
  useEffect(() => {
    if (!start || !end || !space) { setQuote(null); return; }
    let mounted = true;
    setQuoteLoading(true);
    api.post("/public/quote", {
      space_id: space.id, start_time: start, end_time: end,
    })
      .then(({ data }) => {
        if (!mounted) return;
        setQuote(data);
        track("quote_generated", {
          space_id: space.id, total: data.total_avulso, has_upsell: !!data.package_upsell,
        });
      })
      .catch((e) => mounted && toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Não foi possível cotar este horário"))
      .finally(() => mounted && setQuoteLoading(false));
    return () => { mounted = false; };
  }, [start, end, space]);

  const handleSlotChange = ({ start: s, end: e }) => {
    setStart(s); setEnd(e);
    if (s) track("select_slot", { space_id: space?.id, start: s, end: e });
  };

  const handleContinue = () => {
    if (!quote) return;
    const payload = {
      space: {
        id: space.id, name: space.name, category: space.category,
        photo: space.photos?.[0] ? resolvePhoto(space.photos[0]) : null,
      },
      quote,
    };
    saveQuote(payload);
    track("begin_checkout", { space_id: space.id, total: quote.total_avulso });
    nav("/reservar/cotacao");
  };

  const features = useMemo(() => {
    const base = [
      { icon: Wifi, label: "Wi-fi de alta velocidade" },
      { icon: Coffee, label: "Café ilimitado" },
      { icon: Wind, label: "Ar-condicionado" },
    ];
    // future: pull from space.features
    return base;
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        Carregando ambiente...
      </div>
    );
  }
  if (!space) return null;

  const photos = (space.photos || []).map(resolvePhoto).filter(Boolean);

  return (
    <div className="bg-gray-50 min-h-screen pb-32 lg:pb-12" data-testid="espaco-detalhe-page">
      {/* Top nav */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/espacos" className="flex items-center gap-2 text-sm font-semibold text-[#0A1128]" data-testid="back-to-spaces">
            <ArrowLeft size={18} /> Voltar
          </Link>
          <div className="text-xs uppercase tracking-widest text-gray-500 hidden sm:block">
            Reserva online
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 lg:py-10 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8">
        {/* LEFT: gallery + info */}
        <div className="space-y-6">
          {photos.length > 0 ? (
            <SpaceGallery photos={photos} videoUrl={space.video_url} name={space.name} />
          ) : (
            <div className="aspect-[16/10] rounded-2xl bg-gradient-to-br from-[#14213D] to-[#0A1128] flex flex-col items-center justify-center text-white/40">
              <ImageIcon size={52} className="mb-3" />
              <span className="text-xs uppercase tracking-widest">Fotos em breve</span>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-bold mb-2">
              {space.category}
            </div>
            <h1 className="font-display text-3xl lg:text-4xl text-[#0A1128] font-light leading-tight">
              {space.name}
            </h1>
            <div className="flex flex-wrap gap-4 mt-4 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1.5"><Users size={16} /> Capacidade: {space.capacity || 1} pessoa{(space.capacity || 1) > 1 ? "s" : ""}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin size={16} /> R. Sebastião Lacerda, Piabetá — Magé/RJ</span>
            </div>
          </div>

          {space.description && (
            <div>
              <h2 className="font-display text-xl text-[#0A1128] mb-3">Sobre o espaço</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{space.description}</p>
            </div>
          )}

          <div>
            <h2 className="font-display text-xl text-[#0A1128] mb-3">Recursos inclusos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {features.map((f, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm text-gray-700">
                  <CheckCircle2 size={18} className="text-emerald-600 shrink-0" /> {f.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: booking widget */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-5 lg:p-6 space-y-4" data-testid="booking-widget">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-3xl font-black text-[#0A1128] leading-none">
                  {availability?.pricing_unit === "day"
                    ? formatBRL(space.daily_rate)
                    : formatBRL(space.hourly_rate)}
                </div>
                <div className="text-xs uppercase tracking-widest text-gray-500 mt-1">
                  {availability?.pricing_unit === "day" ? "por dia" : "por hora"}
                </div>
                {/* Modalidades alternativas de preço, se configuradas */}
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
                  {space.half_day_rate > 0 && (
                    <span data-testid="price-half-day">Meia diária (4h): <b className="text-[#0A1128]">{formatBRL(space.half_day_rate)}</b></span>
                  )}
                  {space.daily_rate > 0 && availability?.pricing_unit !== "day" && (
                    <span data-testid="price-daily">Diária: <b className="text-[#0A1128]">{formatBRL(space.daily_rate)}</b></span>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Quando?</div>
              <BookingCalendar value={date} onChange={setDate} />
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">
                {availability?.pricing_unit === "day" ? "Reserva do dia" : "Horários disponíveis"}
              </div>
              {slotsLoading ? (
                <div className="text-sm text-gray-400 py-6 text-center">Carregando horários...</div>
              ) : (
                <SlotPicker
                  slots={availability?.slots || []}
                  pricingUnit={availability?.pricing_unit}
                  startTime={start}
                  endTime={end}
                  onChange={handleSlotChange}
                />
              )}
            </div>

            {quote && (
              <div className="border-t border-gray-200 pt-4 space-y-2">
                {quote.pricing_unit === "hour" && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{quote.hours}h × {formatBRL(quote.unit_price)}</span>
                    <span className="text-gray-900 font-semibold">{formatBRL(quote.total_avulso)}</span>
                  </div>
                )}
                {quote.pricing_unit === "half_day" && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Meia Diária (4h)</span>
                    <span className="text-gray-900 font-semibold">{formatBRL(quote.total_avulso)}</span>
                  </div>
                )}
                {quote.pricing_unit === "day" && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Diária</span>
                    <span className="text-gray-900 font-semibold">{formatBRL(quote.total_avulso)}</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline pt-1">
                  <span className="text-sm font-bold text-[#0A1128]">Total</span>
                  <span className="text-2xl font-black text-[#0A1128]">{formatBRL(quote.total_avulso)}</span>
                </div>

                {quote.package_upsell && (
                  <div className="rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/40 p-3 text-xs leading-relaxed">
                    <div className="font-bold text-[#7a5f10] mb-1">💡 Economize com pacote</div>
                    <div className="text-gray-700">{quote.package_upsell.message}</div>
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={handleContinue}
              disabled={!quote || quoteLoading}
              data-testid="continue-to-checkout"
              className={[
                "w-full rounded-xl py-4 font-bold uppercase tracking-wider text-sm transition-all",
                quote
                  ? "bg-[#0A1128] text-white hover:bg-[#14213D] active:scale-[0.99]"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed",
              ].join(" ")}
            >
              {quoteLoading ? "Calculando..." : quote ? "Continuar →" : "Escolha um horário"}
            </button>

            <div className="text-[11px] text-gray-500 text-center">
              Sem fidelidade · Cancelamento grátis até 24h antes
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
